import { Observable, OperatorFunction, pipe } from 'rxjs';
import { debounceTime, filter, startWith } from 'rxjs/operators';

import { BaseEntity, EntityContext, Notification, NotificationType, ORMError, RelationDescription } from '@archipad-js/core/orm';
import { Delegate, DynNode, makePathExpression } from '@archipad-js/core/query';

import { FileDescriptor } from '@core/services/file/file';

export { BaseEntity, EntityContext, DynamicEntities, type Notification, NotificationType, type Validator } from '@archipad-js/core/orm';


// #region Rx operators //
/**
 * Filter some notifications based on their type from notifications pipe
 *
 * @param types
 * @param strict Filter or not the null values
 */
export function keepOnly<T extends Notification>(types: NotificationType[], strict: boolean = false): OperatorFunction<T, T> {
    const strictFilter = (notification: T) => notification ? types.indexOf(notification[0]) !== -1 : false;
    const looseFilter = (notification: T) => !notification || types.indexOf(notification[0]) !== -1;
    
    const filteringFn = strict ? strictFilter : looseFilter;

    return function (source: Observable<T>) {
        return source.pipe(
            filter(filteringFn),
        )
    }
}

/**
 * Filters notifications and keep only those that concerns the given entity names.
 * 
 * @example
 * entityContext.notifications.pipe(
 *   ifEntityConcerned('PlanningEvent', 'PlanningEventCategory'),
 *   ...
 * );
 */
export function ifEntityConcerned<K extends keyof EntityTypeMap, T = EntityTypeMap[K]>(...entityNames: K[]): OperatorFunction<Notification<BaseEntity>, Notification<T>>;
export function ifEntityConcerned<T extends BaseEntity>(...entityNames: string[]): OperatorFunction<Notification<BaseEntity>, Notification<T>> {
    return function(source: Observable<Notification<T>>) {
        return source.pipe(
            keepOnly([
                NotificationType.entityCreated,
                NotificationType.willDeleteEntity, 
                NotificationType.entityDeleted,
                NotificationType.entityUpdated,
                NotificationType.attachmentUpdated,
            ]),
            filter((notification) => {
                if (notification === null) {
                    return true;
                }

                const [, entity] = notification;

                const isConcerned = entityNames.includes(entity?.entityType.entityName);
                return isConcerned;
            }),
        );
    }
}

/**
 * Filters notifications and keep those that are related to the given entity IDs.
 * @example
 * ```typescript
 *   entityContext.notifications.pipe(
 *     ofEntity([{ id: selectedId, visit }]),
 *     ...
 *   );
 * ```

 */
 export function ofEntity<T extends BaseEntity>(allowedEntities: Array<T | Pick<T, "id">>): OperatorFunction<Notification, Notification<T>> {
     return pipe(
         keepOnly( [
             NotificationType.entityCreated,
             NotificationType.willDeleteEntity,
             NotificationType.entityDeleted,
             NotificationType.entityUpdated,
             NotificationType.attachmentUpdated,
         ] ),
         filter( ( notification ): notification is Notification<T> | null => {
             if ( notification === null ) {
                 return true;
             }

             const [ , entity ] = notification ?? [];

             if ( !entity ) {
                 return false;
             }
             const isAllowed = allowedEntities.find( ( entityId => entityId.id === entity.id ) ) !== undefined;
             return isAllowed;
         } ),
    );
}

/**
 * In the context of the given entity : emit only the requested notifications for a subset of entities
 * @param entityNames emit only notifications concerning those types of entity
 * @param notifications filter notifications types
 */
export function fromEntityNotifications<T extends BaseEntity>(
    contextEntity: BaseEntity,
    entityNames: string[],
    notifications: NotificationType[])
    : Observable<Notification<T>>
export function fromEntityNotifications<K extends keyof EntityTypeMap, T = EntityTypeMap[K]>(
    contextEntity: BaseEntity,
    entityNames: K[],
    notifications: NotificationType[])
    : Observable<Notification<T>> {
    return contextEntity
        .getContext()
        .notifications.pipe(
            startWith(null as Notification),
            keepOnly(notifications),
            ifEntityConcerned<K, T>(...entityNames),
            debounceTime(0));
}

/**
 * Emit when entities are created/updated/deleted in the given context
 * @see {@link fromEntityNotifications()}
 */
export function fromEntityChangeNotifications(contextEntity: BaseEntity, ...entityNames: Parameters<typeof ifEntityConcerned>[0][]) {
    return fromEntityNotifications(
        contextEntity,
        entityNames,
        [NotificationType.entityCreated, NotificationType.entityUpdated, NotificationType.entityDeleted]);
}

// #endregion

//------------------------------------------------------------------------------
export type Attachment = FileDescriptor;

export interface AttachmentList {
    [index: string]: Attachment;
}

/**
 * Make a predicate delegate for a given entity type
 * 
 * Usage:
 * ```
 * let predicate = new Predicate.OrderByPredicate('visit.date asc, time asc');
 * let compareFn = predicate.build(Orm.predicateDelegateFor(entityContext, 'Remark'));
 * // compareFn is a comparator function for two RemarkEntity
 * ```
 * 
 * @param entityContext orm entity context
 * @param entityName entity name
 */
export function predicateDelegateFor(entityContext:EntityContext, entityName:string): Delegate<unknown> {
    const entityType = entityContext.entityTypes.byName[entityName];
    if (!entityType) {
        throw new ORMError(`No such entity name "${entityName}"`);
    }
    
    return {
        getProperty(name:string) {
            const keyPathDescription = entityType.keyPathDescription(name);
            for(let i=0;i<keyPathDescription.length-1;i++) {
                const propertyDescription = keyPathDescription[i] as RelationDescription;
                if(!propertyDescription.single) {
                    throw new ORMError(`"${name}" is not a single relationship in ${entityName}`);
                }
            }

            const property = keyPathDescription[keyPathDescription.length-1];
            if (property.relation === true) {
                throw new ORMError("property must be a value property. found a relation");
            }

            return new DynNode(property.type, makePathExpression(name));
        },
    }
}
